import React, { useEffect, useState } from 'react';
import { useConfirm } from '../components/ConfirmDialog';
import {
  pushSupport, permission, currentEndpoint, turnOn, turnOff, sendTest, saveQuiet
} from '../lib/webPush';

/**
 * "Push to your devices" on the Notifications page (migration 129).
 *
 *   THIS DEVICE   Notify this device - asks the browser, subscribes with
 *                 the server's key, and adds it to the list. On an iPhone or
 *                 iPad in a browser tab it explains instead: Apple only
 *                 pushes to Jarvis added to the Home Screen (iOS 16.4+).
 *   YOUR DEVICES  every device this person turned on, this one marked, each
 *                 with when it was added and last used, a test, and Remove.
 *   QUIET HOURS   no pushes in these hours (Malta time) except calls; on by
 *                 default 20:00-07:30, the person's to change or turn off.
 *
 * `push` is the page's data.push (routes/notification-prefs.js); `onData`
 * takes the fresh page the server answers each change with. `readOnly` is
 * View as (looking, not changing).
 */

const Icon = {
  phone: (
    <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="5" y="1.8" width="8" height="14.4" rx="1.8" /><path d="M8 13.6h2" />
    </svg>
  ),
  moon: (
    <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14.8 11.2A6.2 6.2 0 0 1 6.8 3.2a6.2 6.2 0 1 0 8 8Z" />
    </svg>
  )
};

const DAY = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Europe/Malta' });
const dayOf = (v) => (v ? DAY.format(new Date(v)) : null);

export default function NotificationPush({ push, readOnly, onData, say, failed, Switch }) {
  const [support] = useState(pushSupport);
  const [perm, setPerm] = useState(permission);
  const [here, setHere] = useState(null);
  const [busy, setBusy] = useState('');
  const [quiet, setQuiet] = useState(push?.quiet || null);
  const [confirm, confirmDialog] = useConfirm();

  useEffect(() => { setQuiet(push?.quiet || null); }, [push]);
  useEffect(() => {
    let live = true;
    currentEndpoint().then((e) => { if (live) setHere(e); });
    return () => { live = false; };
  }, [push]);

  if (!push) return null;
  const devices = push.devices || [];
  const thisOne = here ? devices.find((d) => d.endpoint === here) : null;

  const run = async (what, fn, done) => {
    setBusy(what);
    try {
      const fresh = await fn();
      if (fresh && fresh.kinds) onData(fresh);
      if (done) say(done);
    } catch (e) {
      failed(e, e?.response ? 'That could not be saved. Try again in a moment.' : (e?.message || 'That did not work.'));
      if (what === 'quiet') setQuiet(push.quiet || null);
    } finally {
      setBusy('');
      setPerm(permission());
    }
  };

  const on = () => run('on', async () => {
    const fresh = await turnOn(push.public_key);
    setHere(await currentEndpoint());
    return fresh;
  }, 'Notifications are on for this device');

  const remove = async (d) => {
    const mine = Boolean(thisOne && thisOne.id === d.id);
    const ok = await confirm({
      title: mine ? 'Turn off notifications on this device?' : `Remove ${d.label}?`,
      message: mine
        ? 'Jarvis will stop pushing notifications to this device. You can turn them on again here at any time.'
        : 'Jarvis will stop pushing notifications to it. To turn it back on, open this page on that device.',
      confirmLabel: mine ? 'Turn off' : 'Remove'
    });
    if (!ok) return;
    await run(`off-${d.id}`, async () => {
      const fresh = await turnOff(d, { here: mine });
      if (mine) setHere(null);
      return fresh;
    }, mine ? 'Turned off on this device' : 'Removed');
  };

  const test = (d) => run(`test-${d.id}`, () => sendTest(d), 'Test sent - it should appear in a moment');

  const changeQuiet = (body) => run('quiet', () => saveQuiet(body), 'Saved');

  let thisDevice;
  if (support === 'ios-install') {
    thisDevice = (
      <p className="ns-push-note" data-testid="ns-push-ios">
        On an iPhone or iPad, notifications only work from Jarvis added to your Home Screen (iOS 16.4 or later).
        In Safari tap Share, then Add to Home Screen, open Jarvis from the new icon, and turn them on here.
      </p>
    );
  } else if (support === 'ios-old') {
    thisDevice = <p className="ns-push-note" data-testid="ns-push-unsupported">This iPhone needs iOS 16.4 or later to receive notifications from Jarvis.</p>;
  } else if (support !== 'ok') {
    thisDevice = <p className="ns-push-note" data-testid="ns-push-unsupported">This browser cannot receive notifications from Jarvis.</p>;
  } else if (thisOne) {
    thisDevice = <p className="ns-push-note" data-tone="success" data-testid="ns-push-here">On for this device.</p>;
  } else if (perm === 'denied') {
    thisDevice = (
      <p className="ns-push-note" data-tone="warning" data-testid="ns-push-blocked">
        Notifications are blocked for Jarvis in this browser. Allow them in the browser’s site settings, then come back here.
      </p>
    );
  } else {
    thisDevice = (
      <button type="button" className="btn btn-primary btn-small ns-push-on" onClick={on}
              disabled={readOnly || Boolean(busy)} data-testid="ns-push-on">
        {busy === 'on' ? 'Turning on…' : 'Notify this device'}
      </button>
    );
  }

  return (
    <section className="ns-card ns-device ns-push" aria-labelledby="ns-push-h" data-testid="ns-push">
      <h2 id="ns-push-h" className="ns-h2">Push to your devices</h2>
      <p className="ns-push-lead">
        A short notice on your phone or computer, even when Jarvis is closed. Choose what is pushed in the Push column below.
      </p>

      {!push.ready && (
        <p className="as-note" data-tone="warning" data-testid="ns-push-not-ready">
          Push notifications are not available on this server yet (migration 129 has not run).
        </p>
      )}
      {push.ready && !push.configured && (
        <p className="as-note" data-tone="info" data-testid="ns-push-off">
          Push notifications are not set up on this server.{push.reason ? ` (${push.reason}.)` : ''}
        </p>
      )}

      {push.ready && push.configured && (
        <>
          <div className="ns-device-row">
            <span className="ns-device-ic">{Icon.phone}</span>
            <div className="ns-device-text">
              <strong>This device</strong>
              {thisDevice}
            </div>
          </div>

          {devices.length > 0 && (
            <ul className="ns-push-list" data-testid="ns-push-devices" aria-label="Your devices">
              {devices.map((d) => {
                const mine = Boolean(thisOne && thisOne.id === d.id);
                return (
                  <li key={d.id} className="ns-push-item" data-device={d.id}>
                    <div className="ns-push-what">
                      <strong>{d.label}{mine && <span className="ns-push-chip">This device</span>}</strong>
                      <span>
                        Added {dayOf(d.created_at)}
                        {d.last_used_at ? ` · last notified ${dayOf(d.last_used_at)}` : ''}
                      </span>
                    </div>
                    {!readOnly && (
                      <div className="ns-push-acts">
                        <button type="button" className="btn btn-plain btn-small" onClick={() => test(d)}
                                disabled={Boolean(busy)} data-testid={`ns-push-test-${d.id}`}>
                          Send a test
                        </button>
                        <button type="button" className="btn btn-outline btn-small" onClick={() => remove(d)}
                                disabled={Boolean(busy)} data-testid={`ns-push-remove-${d.id}`}>
                          {mine ? 'Turn off' : 'Remove'}
                        </button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}

      {push.ready && quiet && (
        <div className="ns-device-row ns-quiet" data-testid="ns-quiet">
          <span className="ns-device-ic">{Icon.moon}</span>
          <div className="ns-device-text">
            <strong>Quiet hours</strong>
            <span>
              {quiet.enabled
                ? `Nothing is pushed between ${quiet.from} and ${quiet.to} (Malta time), except calls.`
                : 'Off: notifications are pushed at any hour.'}
            </span>
            {quiet.enabled && (
              <div className="ns-quiet-times">
                <label>
                  <span>From</span>
                  <input type="time" value={quiet.from} disabled={readOnly || busy === 'quiet'} data-testid="ns-quiet-from"
                         onChange={(e) => setQuiet((q) => ({ ...q, from: e.target.value }))}
                         onBlur={(e) => { if (e.target.value && e.target.value !== push.quiet?.from) changeQuiet({ from: e.target.value }); }} />
                </label>
                <label>
                  <span>To</span>
                  <input type="time" value={quiet.to} disabled={readOnly || busy === 'quiet'} data-testid="ns-quiet-to"
                         onChange={(e) => setQuiet((q) => ({ ...q, to: e.target.value }))}
                         onBlur={(e) => { if (e.target.value && e.target.value !== push.quiet?.to) changeQuiet({ to: e.target.value }); }} />
                </label>
                {quiet.set && !readOnly && (
                  <button type="button" className="ns-test" onClick={() => changeQuiet({ reset: true })} data-testid="ns-quiet-reset">
                    Back to {push.quiet_default?.from || '20:00'}–{push.quiet_default?.to || '07:30'}
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="ns-device-act">
            <Switch on={quiet.enabled} label="Quiet hours" busy={busy === 'quiet'} disabled={readOnly}
                    onChange={() => changeQuiet({ enabled: !quiet.enabled })} testid="ns-quiet-switch" />
          </div>
        </div>
      )}
      {confirmDialog}
    </section>
  );
}
